// src/components/homeComponents/CaseStudies.jsx
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import web4 from "../../assets/websites/w4.png"; // Coolest Parfume mockup
import p1 from "../../assets/1e.png";
import p2 from "../../assets/2e.png";
import p3 from "../../assets/3e.png";
import p4 from "../../assets/4e.png";
import p5 from "../../assets/5e.png";

gsap.registerPlugin(ScrollTrigger);

const PUB = process.env.PUBLIC_URL || "";
const design = (name) => `${PUB}/designs/${encodeURIComponent(name)}`;

/*
  CASE STUDIES — a pinned, horizontally-scrolled showreel.
  Each panel is a "frame" of the film: a real client, one big number that
  counts up as it lands, one line of result, and a tag.
  NOTE for Amiri: swap the `value` numbers below for your real figures.
*/
const CASES = [
  {
    id: "01",
    brand: "Orhidea Perfume",
    type: "Fragrance · From 0 to millions",
    image: p1,
    value: 7.4,
    decimals: 1,
    suffix: "M",
    metricLabel: "organic views on Tiktok and Instagram",
    line: "From an unknown brand to thousands of sales with no ads. We took over in the very beginning of this brand, redesigned the logo and started making content that actually sold and got the attention of the right audience",
    accent: "#c89b6b",
  },
  {
    id: "02",
    brand: "Travelia",
    type: "Travel Agency · Organic Content",
    image: p2,
    value: 6.3,
    suffix: "M",
    decimals: 1,
    metricLabel: "views in 2 months",
    line: "We took this client while they were spending a lot in ads daily but no results. With organic content we took them to more than 16k followers in Instagram and Tiktok alongside millions of views, and of couse - A LOT of sales. Now their biggest problem is that they have too many people coming in 👀",
    accent: "#f2b134",
  },
  {
    id: "03",

    brand: "Pretty Needs",
    type: "Health Product · Organic Content",
    image: p3,
    value: 418,
    prefix: "+",
    suffix: "%",
    metricLabel: "Sales, followers and views",
    line: "We turned a brand that had 30 followers to a brand that sells every single day and it's trusted by everyone.",
    accent: "#e6a3b3",
  },
  {
    id: "04",
    brand: "Siara Beauty",
    type: "Beauty Product · Organic Content + Website",
    image: p4,
    value: 70,
    prefix: "",
    suffix: "+",
    metricLabel: "new sales with 0 ads",
    line: "Again from 0 to hero. We built them a website and started making organic content, now they're selling every single day",
    accent: "#4ea9d6",
  },
  {
    id: "05",
    brand: "Raimonda Beauty",
    type: "Beauty Sallon · Organic Content + Website with Booking System",
    image: p5,
    value: 750,
    prefix: "+",
    suffix: "k",
    rawText: "",
    metricLabel: "new views since we took over",
    line: "We first built their website with an amazing integrated booking system, and recently we started making content for them. It's going absolutely amazing!",
    accent: "#8fc15a",
  },
  // {
  //   id: "06",
  //   brand: "Coolest Parfume",
  //   type: "E-commerce · Web Build",
  //   image: web4,
  //   value: 100,
  //   suffix: "%",
  //   metricLabel: "custom, no templates",
  //   line: "An order experience that feels like the product.",
  //   accent: "#9281dd",
  // },
];

const CaseStudies = () => {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const trackRef = useRef(null);
  const imgRefs = useRef([]);
  const numRefs = useRef([]);
  const counted = useRef({});
  const [active, setActive] = useState(0);

  const formatVal = (c, n) => {
    const dec = c.decimals || 0;
    const num = dec ? n.toFixed(dec) : Math.round(n).toLocaleString();
    return `${c.prefix || ""}${num}${c.suffix || ""}`;
  };

  const runCounter = (i) => {
    const c = CASES[i];
    const node = numRefs.current[i];
    if (!node) return;
    if (c.rawText) {
      node.textContent = c.rawText;
      return;
    }
    if (counted.current[i]) return;
    counted.current[i] = true;
    const obj = { v: 0 };
    gsap.to(obj, {
      v: c.value,
      duration: 1.4,
      ease: "power3.out",
      onUpdate: () => {
        node.textContent = formatVal(c, obj.v);
      },
    });
  };

  useEffect(() => {
    const section = sectionRef.current;
    const pin = pinRef.current;
    const track = trackRef.current;
    if (!section || !pin || !track) return;

    let st;
    const build = () => {
      const distance = Math.max(track.scrollWidth - window.innerWidth, 0);
      // total scroll length: gives each frame breathing room
      const total = distance + window.innerHeight * 0.6;

      st = ScrollTrigger.create({
        trigger: section,
        start: "top top",
        end: `+=${total}`,
        pin: pin,
        scrub: 1,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        onUpdate: (self) => {
          const p = self.progress;
          gsap.set(track, { x: -p * distance });

          // active frame
          const idx = Math.min(
            CASES.length - 1,
            Math.round(p * (CASES.length - 1)),
          );
          setActive(idx);
          runCounter(idx);

          // per-frame parallax on the image
          const center = window.innerWidth / 2;
          imgRefs.current.forEach((el) => {
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const elCenter = rect.left + rect.width / 2;
            const dist = (elCenter - center) / window.innerWidth;
            gsap.set(el, { xPercent: dist * -14, scale: 1 });
          });
        },
      });
    };

    // build after layout/images settle
    const t = setTimeout(() => {
      build();
      ScrollTrigger.refresh();
    }, 120);
    window.addEventListener("load", () => ScrollTrigger.refresh());

    return () => {
      clearTimeout(t);
      if (st) st.kill();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Section ref={sectionRef}>
      <Pin ref={pinRef}>
        <Heading>
          <span className="kicker">SELECTED WORK</span>
          <h2>
            Results we <em>screenshot</em>.
          </h2>
        </Heading>

        <Counter>
          <span className="now">{CASES[active].id}</span>
          <span className="sep">/</span>
          <span className="total">{CASES[CASES.length - 1].id}</span>
        </Counter>

        <Track ref={trackRef}>
          {CASES.map((c, i) => (
            <Panel key={c.id} $active={i === active}>
              <div className="frame">
                <img
                  ref={(el) => (imgRefs.current[i] = el)}
                  src={c.image}
                  alt={c.brand}
                />
                <div className="glow" style={{ background: c.accent }} />
              </div>
              <div className="meta">
                <span className="type" style={{ color: c.accent }}>
                  {c.type}
                </span>
                <h3>{c.brand}</h3>
                <div className="metric">
                  <span
                    className="value"
                    ref={(el) => (numRefs.current[i] = el)}
                    style={{ color: c.accent }}
                  >
                    {c.rawText
                      ? c.rawText
                      : `${c.prefix || ""}0${c.suffix || ""}`}
                  </span>
                  <span className="label">{c.metricLabel}</span>
                </div>
                <p className="line">{c.line}</p>
              </div>
            </Panel>
          ))}
        </Track>

        <Progress>
          <span style={{ width: `${((active + 1) / CASES.length) * 100}%` }} />
        </Progress>
      </Pin>
    </Section>
  );
};

const Section = styled.section`
  position: relative;
  background: #08040c;
`;

const Pin = styled.div`
  height: 100vh;
  width: 100%;
  overflow: hidden;
  position: relative;
  display: flex;
  align-items: center;
`;

const Heading = styled.div`
  position: absolute;
  top: 8vh;
  left: 7%;
  z-index: 5;
  pointer-events: none;
  .kicker {
    font-family: "Montserrat";
    letter-spacing: 0.45em;
    font-size: 0.72rem;
    color: #836caf;
    font-weight: 500;
  }
  h2 {
    font-family: "Readex Pro", sans-serif;
    font-weight: 200;
    font-size: clamp(2rem, 4.5vw, 4rem);
    line-height: 1;
    margin-top: 0.4em;
    em {
      font-style: italic;
      color: #9281dd;
    }
  }
  @media (max-width: 700px) {
    top: 5vh;
  }
`;

const Counter = styled.div`
  position: absolute;
  top: 9vh;
  right: 7%;
  z-index: 5;
  font-family: "Readex Pro", sans-serif;
  color: #ffffff;
  .now {
    font-size: clamp(2rem, 4vw, 3.4rem);
    font-weight: 300;
  }
  .sep {
    margin: 0 0.25em;
    color: #463d6e;
  }
  .total {
    color: #6a6080;
    font-size: 1.2rem;
  }
  @media (max-width: 600px) {
    display: none;
  }
`;

const Track = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6vw;
  padding: 0 14vw;
  will-change: transform;
  @media (max-width: 820px) {
    padding: 0 8vw;
  }
`;

const Panel = styled.article`
  flex: 0 0 auto;
  width: 64vw;
  max-width: 980px;
  display: flex;
  align-items: center;
  gap: 3vw;
  transition:
    opacity 0.5s ease,
    filter 0.5s ease;
  opacity: ${({ $active }) => ($active ? 1 : 0.32)};
  filter: ${({ $active }) => ($active ? "none" : "blur(2px)")};

  .frame {
    position: relative;
    flex: 0 0 56%;
    aspect-ratio: 6 / 7;
    border-radius: 22px;
    overflow: hidden;
    border: 1px solid #221a33;
    box-shadow: 0 40px 120px -40px #000;
    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      will-change: transform;
    }
    .glow {
      position: absolute;
      width: 60%;
      height: 40%;
      bottom: -20%;
      left: 20%;
      filter: blur(90px);
      opacity: 0.35;
      border-radius: 50%;
      z-index: -1;
    }
  }

  .meta {
    flex: 1;
    .type {
      font-family: "Montserrat";
      letter-spacing: 0.2em;
      font-size: 0.7rem;
      font-weight: 600;
      text-transform: uppercase;
    }
    h3 {
      font-family: "Readex Pro", sans-serif;
      font-weight: 300;
      font-size: clamp(1.6rem, 3vw, 2.8rem);
      margin: 0.3em 0 0.6em;
      line-height: 1;
    }
    .metric {
      border-top: 1px solid #221a33;
      padding-top: 1em;
      .value {
        display: block;
        font-family: "Readex Pro", sans-serif;
        font-weight: 200;
        font-size: clamp(2.6rem, 6vw, 5rem);
        line-height: 0.9;
      }
      .label {
        color: #8a8595;
        font-size: 0.9rem;
      }
    }
    .line {
      margin-top: 1.4em;
      color: #cfcad9;
      font-size: clamp(0.95rem, 1.3vw, 1.15rem);
      max-width: 26ch;
      line-height: 1.5;
    }
  }

  @media (max-width: 820px) {
    width: 84vw;
    flex-direction: column;
    gap: 1.2em;
    .frame {
      flex: 0 0 auto;
      width: 90%;
      aspect-ratio: 11 / 11;
      img {
        object-fit: contain;
      }
    }
    .meta {
      width: 100%;
      h3 {
        font-size: clamp(1.3rem, 6vw, 1.8rem);
        margin: 0.2em 0 0.4em;
      }
      .metric {
        padding-top: 0.6em;
        .value {
          font-size: clamp(2rem, 10vw, 3rem);
        }
        .label {
          font-size: 0.8rem;
        }
      }
      .line {
        margin-top: 0.8em;
        font-size: 0.7rem;
        max-width: none;
      }
    }
  }
`;

const Progress = styled.div`
  position: absolute;
  bottom: 5vh;
  left: 7%;
  right: 7%;
  height: 2px;
  background: #1b1426;
  z-index: 5;
  span {
    display: block;
    height: 100%;
    background: linear-gradient(90deg, #9281dd, #b9c5fb);
    transition: width 0.4s ease;
  }
`;

export default CaseStudies;
